import { CompanyEntity } from 'shared/domain/company/types/entity';
import type {
  CompanyDeleteRequest,
  CompanyRestoreRequest,
  CreateCompanyRequest,
  EditCompanyRequest,
} from 'shared/domain/messages/company/eventMessages';
import { DomainMessagesTypes, Services } from 'shared/domain/messages/message';
import { broadcastAllCompanies } from 'serviceWorker/broadcasts/company/all';
import * as companies from 'serviceWorker/db/companies/companies';
import * as companiesService from 'serviceWorker/db/companies/companiesService';
import { debugLog } from 'shared/logger/debugLog';
import { awaitEntities } from '../factories/awaitEntities';
import { waitForMessage } from '../factories/waitForMessage';
import { get as getSelectedProject } from 'serviceWorker/db/selectedProject';
import { companySaver } from './addCompany';
import { companyDeleter } from './deleteCompany';
import { companyEditor } from './editCompany';
import { fetchCompanies, fetchUpdatedCompanies } from './fetchCompanies';
import { companyRestorer } from './restoreCompany';
import { companyContract } from 'shared/domain/company/companyContract';
import { createSyncedEntity } from '../factories/createSyncedEntity';

/** Download and sync of the companies; the source's `sync` is its entry point. */
export const companiesSync = createSyncedEntity({
  entityName: Services.COMPANIES,
  contract: companyContract,
  repository: companies,
  service: companiesService,
  fetch: fetchCompanies,
  fetchUpdated: fetchUpdatedCompanies,
  publishCollection: broadcastAllCompanies,
});

const waitForCompanies = waitForMessage(
  companyContract,
  DomainMessagesTypes.allCompanies
);

/**
 * Resolves once the change is in the HTTP queue and rejects only when it could
 * not be queued. The server's answer follows as an event (`createdEntity`,
 * `failUploadedEntity`, …) with the change's `uniqueId`.
 */
export function handleAddCompany(detail: CreateCompanyRequest): Promise<void> {
  debugLog('addCompany', detail);
  return companySaver.execute(detail.companyCreateModel, detail.uniqueId);
}

export function handleEditCompany(detail: EditCompanyRequest): Promise<void> {
  debugLog('editCompany', detail);
  return companyEditor.execute(detail.companyEditModel, detail.uniqueId);
}

/** Archive and restore address the company within the selected project. */
async function getSelectedProjectId(): Promise<string> {
  const selectedProject = await getSelectedProject();
  if (!selectedProject) {
    throw new Error('No project selected');
  }
  return selectedProject._id;
}

export async function handleDeleteCompany(
  detail: CompanyDeleteRequest
): Promise<void> {
  debugLog('deleteCompany', detail);
  await companyDeleter.execute({
    companyId: detail.companyId,
    uniqueId: detail.uniqueId,
    projectId: await getSelectedProjectId(),
  });
}

export async function handleRestoreCompany(
  detail: CompanyRestoreRequest
): Promise<void> {
  debugLog('restoreCompany', detail);
  await companyRestorer.execute({
    companyId: detail.companyId,
    uniqueId: detail.uniqueId,
    projectId: await getSelectedProjectId(),
  });
}

export async function getCompaniesByIds(
  ids: string[]
): Promise<CompanyEntity[]> {
  await awaitEntities(companiesService, waitForCompanies);
  return companies.getByIds(ids);
}

export async function getCompanies(): Promise<CompanyEntity[]> {
  await awaitEntities(companiesService, waitForCompanies);
  return companies.get();
}

